// src/hooks/useReports.ts
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useFormatting } from '../context/FormattingContext';
import { MonthlyRollup, CategoryBreakdown, TrendsSeriesPoint, BudgetVsActualPoint } from '../types/ReportsTrends';
import { buildQueryParams } from '../utils/buildQueryParams';
import { authFetch } from '../lib/authFetch';

type CommonParams = {
  month?: string;
  startDate?: string;
  endDate?: string;
  includeTentative?: boolean;
  isRecurring?: boolean;
  type?: 'income'|'expense'|'savings';
  category?: string;
  showFuture?: boolean;
  tags?: string[];
};

// ✅ Always return a valid HeadersInit
const buildHeaders = (token?: string | null): HeadersInit => {
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

export function useMonthlyRollups(params: CommonParams) {
  const { auth } = useAuth();
  const formatting = useFormatting();
  return useQuery<MonthlyRollup[]>({
    queryKey: ['reports','monthly', params, formatting.timeZone],
    queryFn: async () => {
      const r = await authFetch(`/api/reports/monthly-rollups?${buildQueryParams(params, {
        clampEndDateToTodayWhenShowFutureFalse: true,
        timeZone: formatting.timeZone,
      })}`, {
        headers: buildHeaders(auth?.token),
      });
      if (!r.ok) throw new Error('monthly-rollups failed');
      return r.json();
    },
    staleTime: 60_000,
  });
}

export function useCategoryBreakdown(params: CommonParams) {
  const { auth } = useAuth();
  const formatting = useFormatting();
  return useQuery<CategoryBreakdown>({
    queryKey: ['reports','category', params, formatting.timeZone],
    queryFn: async () => {
      const r = await authFetch(`/api/reports/category-breakdown?${buildQueryParams(params, {
        clampEndDateToTodayWhenShowFutureFalse: true,
        timeZone: formatting.timeZone,
      })}`, {
        headers: buildHeaders(auth?.token),
      });
      if (!r.ok) throw new Error('category-breakdown failed');
      return r.json();
    },
    staleTime: 60_000,
  });
}

export function useTrends(params: CommonParams) {
  const { auth } = useAuth();
  const formatting = useFormatting();
  return useQuery<TrendsSeriesPoint[]>({
    queryKey: ['reports','trends', params, formatting.timeZone],
    queryFn: async () => {
      const r = await authFetch(`/api/reports/trends?${buildQueryParams(params, {
        clampEndDateToTodayWhenShowFutureFalse: true,
        timeZone: formatting.timeZone,
      })}`, {
        headers: buildHeaders(auth?.token),
      });
      if (!r.ok) throw new Error('trends failed');
      return r.json();
    },
    staleTime: 60_000,
  });
}

export function useBudgetVsActual(params: CommonParams) {
  const { auth } = useAuth();
  const formatting = useFormatting();
  return useQuery<{ series: BudgetVsActualPoint[] }>({
    queryKey: ['reports','bva', params, formatting.timeZone],
    queryFn: async () => {
      const r = await authFetch(`/api/reports/budget-vs-actual?${buildQueryParams(params, {
        clampEndDateToTodayWhenShowFutureFalse: true,
        timeZone: formatting.timeZone,
      })}`, {
        headers: buildHeaders(auth?.token),
      });
      if (!r.ok) throw new Error('budget-vs-actual failed');
      return r.json();
    },
    staleTime: 60_000,
  });
}

export function useSavingsTrajectory(params: CommonParams) {
  const { auth } = useAuth();
  const formatting = useFormatting();
  return useQuery<{ savings: { inRange: number; cumulative: number } }>({
    queryKey: ['reports','savings', params, formatting.timeZone],
    queryFn: async () => {
      const r = await authFetch(`/api/reports/savings-trajectory?${buildQueryParams(params, {
        clampEndDateToTodayWhenShowFutureFalse: true,
        timeZone: formatting.timeZone,
      })}`, {
        headers: buildHeaders(auth?.token),
      });
      if (!r.ok) throw new Error('savings-trajectory failed');
      return r.json();
    },
    staleTime: 60_000,
  });
}
